// cardChart — the shared card's chart, drawn rather than photographed.
//
// ⚠️ THE CARD USED TO PHOTOGRAPH THE ON-SCREEN SVG, AND INHERITED EVERYTHING
// WRONG WITH IT. Three separate complaints turned out to be one cause:
//
//   • The chart would not fill the card. A chart rendered in a phone message
//     bubble is roughly square; the Facebook card is 1.9:1. Fitting a square
//     picture into a wide frame at a uniform scale leaves margins, and the only
//     alternative — stretching — squashed every axis price the one time it was
//     tried. Neither option was ever going to be right, because the picture's
//     shape was decided by a phone rather than by the card.
//
//   • A grey vertical line ran through it: the crosshair from wherever the
//     reader last touched the chart, captured along with everything else.
//
//   • Two shares of the same series looked different, because a desktop bubble
//     and a phone bubble are different shapes.
//
// Drawing from the DATA fixes all three at once and cannot regress into any of
// them: the chart is laid out for the box it is given, nothing transient is in
// the source to capture, and the same series produces the same card from any
// device.
//
// ⚠️ IT DRAWS EVERY SHAPE THE CONSULTANT CAN EMIT — a line or several lines
// over dates, and bars over categories. It started with one line only, which
// left the commonest interesting charts (Choice against Select, barn against
// barn) still being photographed and still carrying every fault above. A fix
// that covers the easy half of the cases is not a fix.
//
// What it will not do is guess. A payload it cannot read as one of those two
// shapes returns nothing from cardSeries and is photographed, because a card
// that redraws a chart it does not fully understand is worse than one that
// fits a picture badly.

export interface CardPoint {
  date: string;
  value: number;
}

export interface CardSeries {
  /** Empty for a single unnamed line; the legend is then omitted. */
  name: string;
  points: CardPoint[];
}

/**
 * The palette for several lines on one card.
 *
 * ⚠️ DISTINGUISHABLE WITHOUT COLOUR TOO. A forwarded card is read on any screen
 * and printed in black on some of them, so the lines also differ in weight and
 * the legend sits beside the names rather than relying on hue alone.
 */
export const SERIES_COLORS = ["#1E4634", "#C08A2E", "#5A7D9A", "#8C5A3C"] as const;

/**
 * Round numbers for the value axis: at most `count` of them, on a 1/2/5 step,
 * covering the data.
 *
 * ⚠️ NOT min/max SPLIT EVENLY. That yields labels like $373.86 and $358.86 —
 * the actual first two the card produced — which read as data points rather
 * than as a scale, and invite someone to quote them as prices.
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || count < 2) return [];
  if (max === min) return [min];
  const raw = (max - min) / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  const first = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = first; v <= max + step * 1e-9; v += step) {
    out.push(Number(v.toFixed(10)));
  }
  return out;
}

/**
 * Tangents for a monotone cubic through `pts`.
 *
 * ⚠️ MONOTONE, NOT CATMULL-ROM. A plain spline overshoots at a turn, so a
 * smoothed price line dips BELOW its own low between two points — inventing a
 * price the market never printed. On a card captioned with a USDA report that
 * is not a cosmetic difference.
 */
export function monotoneTangents(pts: CardPoint[]): number[] {
  const n = pts.length;
  if (n < 2) return new Array(n).fill(0);
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) slope.push(pts[i + 1].value - pts[i].value);

  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    // A turning point gets a flat tangent, which is what stops the overshoot.
    m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const h = Math.hypot(a, b);
    if (h > 3) {
      m[i] = ((3 / h) * a) * slope[i];
      m[i + 1] = ((3 / h) * b) * slope[i];
    }
  }
  return m;
}

/** Evenly spaced label positions, always including the first and last point. */
export function labelIndices(n: number, want: number): number[] {
  if (n <= 0) return [];
  if (n <= want) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (want - 1);
  const out = new Set<number>();
  for (let i = 0; i < want; i++) out.add(Math.round(i * step));
  return [...out].sort((a, b) => a - b);
}

/** "2026-08-18" -> "Aug 18". */
export function tickDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.valueOf())
    ? iso
    : d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
}

export interface CardChartStyle {
  line: string;
  /** Bars above and below zero. Green up, red down, as everywhere else. */
  up?: string;
  down?: string;
  fillTop: string;
  fillBottom: string;
  axis: string;
  grid: string;
  font: string;
  labelSize: number;
}

/**
 * Draw the series into the box, filling it.
 *
 * The box is the card's, not the chart's — which is the whole point of drawing
 * rather than photographing.
 */
export function drawCardChart(
  ctx: CanvasRenderingContext2D,
  input: CardPoint[] | CardSeries[],
  box: { x: number; y: number; w: number; h: number },
  style: CardChartStyle,
  formatValue: (v: number) => string,
  opts: { bars?: boolean } = {},
): void {
  const all: CardSeries[] =
    Array.isArray(input) && input.length > 0 && "points" in (input[0] as CardSeries)
      ? (input as CardSeries[])
      : [{ name: "", points: input as CardPoint[] }];
  const series = all.filter((s) => s.points.length >= (opts.bars ? 1 : 2));
  if (!series.length) return;
  const points = series[0].points;
  const named = series.filter((s) => s.name).length > 1;

  // ⚠️ BARS RUN ACROSS, NOT UP. Twelve months or a dozen barn names will not
  // fit as upright labels under upright bars — they collide, and abbreviating
  // them means dropping months out of a seasonal chart. Laid on their side the
  // names read straight and the length of the bar is still the value. It is
  // also how the chart is drawn in the app, so the card does not contradict the
  // screen it came from.
  if (opts.bars) {
    drawHorizontalBars(ctx, points, box, style, formatValue);
    return;
  }

  const L = style.labelSize;
  // Room for the value labels down the left and the dates along the bottom.
  const gutterL = Math.round(L * 4.6);
  const gutterB = Math.round(L * (named && !opts.bars ? 3.6 : 2.4));
  const inset = Math.round(L * 0.9);
  const px = box.x + gutterL;
  const py = box.y + inset;
  const pw = box.w - gutterL - inset;
  const ph = box.h - gutterB - inset;
  if (pw <= 0 || ph <= 0) return;

  const vals = series.flatMap((s) => s.points.map((p) => p.value));
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  // ⚠️ HEADROOM, SO THE LINE NEVER TOUCHES THE FRAME. A line that runs along
  // the top edge reads as clipped — as though the card cut off a higher price.
  const padV = (hi - lo || Math.abs(hi) || 1) * 0.12;
  // ⚠️ BARS ARE MEASURED FROM ZERO. A bar chart whose baseline is the lowest
  // value draws a bar of almost no height for a figure barely below its peers
  // and one of full height for a figure barely above — the picture then says
  // something about the data that the data does not say. Lines are free to
  // start where the data starts; bars are not.
  const yLo = opts.bars ? Math.min(0, lo) : lo - padV;
  const yHi = hi + padV;
  const X = (i: number) => px + (pw * i) / Math.max(1, points.length - 1);
  const Y = (v: number) => py + ph - ((v - yLo) / (yHi - yLo)) * ph;

  ctx.save();
  ctx.textBaseline = "middle";

  const ticks = niceTicks(lo, hi, 4);
  ctx.font = `${L}px ${style.font}`;
  for (const t of ticks) {
    const y = Y(t);
    ctx.strokeStyle = style.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(px, Math.round(y) + 0.5);
    ctx.lineTo(px + pw, Math.round(y) + 0.5);
    ctx.stroke();
    ctx.fillStyle = style.axis;
    ctx.textAlign = "right";
    ctx.fillText(formatValue(t), px - Math.round(L * 0.6), y);
  }

  {
    series.forEach((s, si) => {
      const pts = s.points;
      const m = monotoneTangents(pts);
      const SX = (i: number) => px + (pw * i) / Math.max(1, pts.length - 1);
      const trace = () => {
        ctx.beginPath();
        ctx.moveTo(SX(0), Y(pts[0].value));
        for (let i = 0; i < pts.length - 1; i++) {
          const x0 = SX(i), x1 = SX(i + 1);
          const dx = (x1 - x0) / 3;
          ctx.bezierCurveTo(
            x0 + dx, Y(pts[i].value + m[i] / 3),
            x1 - dx, Y(pts[i + 1].value - m[i + 1] / 3),
            x1, Y(pts[i + 1].value),
          );
        }
      };

      // ⚠️ THE AREA FILL BELONGS TO A SINGLE LINE ONLY. Two translucent fills
      // overlap into a third shade that reads as a band the data never had.
      if (series.length === 1) {
        const grad = ctx.createLinearGradient(0, py, 0, py + ph);
        grad.addColorStop(0, style.fillTop);
        grad.addColorStop(1, style.fillBottom);
        trace();
        ctx.lineTo(SX(pts.length - 1), py + ph);
        ctx.lineTo(SX(0), py + ph);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
      }

      trace();
      ctx.strokeStyle = SERIES_COLORS[si % SERIES_COLORS.length];
      ctx.lineWidth = Math.max(2, Math.round(L * (si === 0 ? 0.22 : 0.17)));
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      if (si > 0) ctx.setLineDash([Math.round(L * 0.5), Math.round(L * 0.42)]);
      ctx.stroke();
      ctx.setLineDash([]);

      // The last print of each line, marked.
      ctx.fillStyle = SERIES_COLORS[si % SERIES_COLORS.length];
      ctx.beginPath();
      ctx.arc(SX(pts.length - 1), Y(pts[pts.length - 1].value), Math.max(3, L * 0.3), 0, Math.PI * 2);
      ctx.fill();
    });
  }

  ctx.fillStyle = style.axis;
  ctx.font = `${L}px ${style.font}`;
  const idx = labelIndices(points.length, 5);
  for (const i of idx) {
    const x = X(i);
    ctx.textAlign = i === 0 ? "left" : i === points.length - 1 ? "right" : "center";
    const pt = points[i];
    ctx.fillText(pt.date ? tickDate(pt.date) : "", x, py + ph + Math.round(L * 1.3));
  }

  // The legend, only when there is more than one named line to tell apart.
  if (named) {
    let lx = px;
    const ly = py + ph + Math.round(L * 2.7);
    ctx.textAlign = "left";
    ctx.font = `600 ${Math.round(L * 0.92)}px ${style.font}`;
    series.forEach((s, si) => {
      const c = SERIES_COLORS[si % SERIES_COLORS.length];
      ctx.fillStyle = c;
      ctx.fillRect(lx, ly - 2, Math.round(L * 0.9), 4);
      lx += Math.round(L * 1.2);
      ctx.fillStyle = style.axis;
      ctx.fillText(s.name, lx, ly);
      lx += ctx.measureText(s.name).width + Math.round(L * 1.4);
    });
  }

  ctx.restore();
}

/**
 * Bars on their side: names down the left, value along the bottom.
 *
 * ⚠️ THE BASELINE IS ZERO AND THE SIGN IS IN THE COLOUR. A seasonal chart is
 * mostly a question of which months go up and which go down; a bar drawn from
 * the lowest value instead of from zero answers it wrongly, and one drawn in a
 * single colour does not answer it at all.
 */
function drawHorizontalBars(
  ctx: CanvasRenderingContext2D,
  points: CardPoint[],
  box: { x: number; y: number; w: number; h: number },
  style: CardChartStyle,
  formatValue: (v: number) => string,
): void {
  if (!points.length) return;
  const L = style.labelSize;
  ctx.save();
  ctx.textBaseline = "middle";
  ctx.font = `${L}px ${style.font}`;

  // ⚠️ THE NAME COLUMN IS MEASURED, NOT GUESSED. "September" and "Beaver County
  // Stockyards" are not the same width, and a fixed column either clips the long
  // ones or steals the chart's room for the short ones. Capped at a third so a
  // runaway label cannot leave no chart at all.
  const widest = Math.max(...points.map((p) => ctx.measureText(p.date).width));
  const gutterL = Math.min(Math.round(widest + L * 0.9), Math.round(box.w * 0.34));
  const gutterB = Math.round(L * 2.2);
  const inset = Math.round(L * 0.9);
  const px = box.x + gutterL;
  const py = box.y + inset;
  const pw = box.w - gutterL - inset;
  const ph = box.h - gutterB - inset;
  if (pw <= 0 || ph <= 0) { ctx.restore(); return; }

  const vals = points.map((p) => p.value);
  // ⚠️ ZERO IS ALWAYS IN RANGE — that is the baseline every bar is measured
  // from. The headroom is added only at the far end, so the longest bar stops
  // short of the name column instead of running into it, and zero stays exactly
  // where the data puts it rather than drifting to make room.
  const rawLo = Math.min(0, ...vals);
  const rawHi = Math.max(0, ...vals);
  const head = (rawHi - rawLo || 1) * 0.06;
  const lo = rawLo < 0 ? rawLo - head : 0;
  const hi = rawHi > 0 ? rawHi + head : 0;
  const span = hi - lo || 1;
  const X = (v: number) => px + ((v - lo) / span) * pw;
  const zero = X(0);

  const slot = ph / points.length;
  const bh = Math.max(2, slot * 0.62);
  const up = style.up ?? style.line;
  const down = style.down ?? style.line;

  const ticks = niceTicks(lo, hi, 3);
  ctx.strokeStyle = style.grid;
  ctx.lineWidth = 1;
  for (const t of ticks) {
    const x = Math.round(X(t)) + 0.5;
    ctx.beginPath();
    ctx.moveTo(x, py);
    ctx.lineTo(x, py + ph);
    ctx.stroke();
    ctx.fillStyle = style.axis;
    ctx.textAlign = "center";
    ctx.fillText(formatValue(t), X(t), py + ph + Math.round(L * 1.2));
  }

  points.forEach((p, i) => {
    const cy = py + slot * (i + 0.5);
    const x = X(p.value);
    ctx.fillStyle = p.value >= 0 ? up : down;
    ctx.fillRect(Math.min(x, zero), cy - bh / 2, Math.abs(x - zero), bh);
    ctx.fillStyle = style.axis;
    ctx.textAlign = "right";
    ctx.fillText(p.date, px - Math.round(L * 0.5), cy);
  });

  // The zero line last, so it reads over the bars rather than under them.
  ctx.strokeStyle = style.axis;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(Math.round(zero) + 0.5, py);
  ctx.lineTo(Math.round(zero) + 0.5, py + ph);
  ctx.stroke();

  ctx.restore();
}
